import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    Alert,
    Badge,
    Button,
    Container,
    Spinner,
} from "react-bootstrap";

import {
    DataGrid,
    GridToolbar,
} from "@mui/x-data-grid";

import moment from "moment";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import DynamicBreadcrumb from "../header/breadcrumb";
import customerApi from "../../services/customerapi";

export default function OutOfZoneCustomerAddresses() {
    const navigate = useNavigate();

    const [rows, setRows] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [paginationModel, setPaginationModel] =
        useState({
            page: 0,
            pageSize: 15,
        });

    const [sortModel, setSortModel] =
        useState([
            {
                field: "createdAt",
                sort: "desc",
            },
        ]);

    const [filterModel, setFilterModel] =
        useState({
            items: [],
            quickFilterValues: [],
        });

    const searchText = useMemo(() => {
        return (
            filterModel?.quickFilterValues
                ?.join(" ")
                ?.trim()
            || ""
        );
    }, [filterModel]);

    const columns = useMemo(
        () => [
            {
                field: "sno",
                headerName: "S NO",
                width: 80,
                sortable: false,
                filterable: false,
            },
            {
                field: "customerName",
                headerName: "Customer",
                minWidth: 190,
                flex: 1,

                renderCell: (params) => (
                    <div>
                        <div className="fw-semibold">
                            {params.row.customerName || "-"}
                        </div>

                        <div className="small text-muted">
                            {params.row.customerMobile || "-"}
                        </div>
                    </div>
                ),
            },
            {
                field: "addressName",
                headerName: "Address Name",
                minWidth: 160,
                flex: 0.8,
            },
            {
                field: "address",
                headerName: "Address",
                minWidth: 300,
                flex: 1.6,

                renderCell: (params) => {
                    const fullAddress = [
                        params.row.doorFlatNo,
                        params.row.address,
                        params.row.landmark,
                        params.row.area,
                        params.row.city,
                        params.row.state,
                        params.row.pincode,
                    ]
                        .filter(Boolean)
                        .join(", ");

                    return (
                        <div
                            style={{
                                whiteSpace: "normal",
                                lineHeight: 1.4,
                                paddingTop: 8,
                                paddingBottom: 8,
                            }}
                        >
                            {fullAddress || "-"}
                        </div>
                    );
                },
            },
            {
                field: "coordinates",
                headerName: "Coordinates",
                minWidth: 190,
                sortable: false,
                filterable: false,

                renderCell: (params) => (
                    <div className="small">
                        <div>
                            Lat: {params.row.latitude || "-"}
                        </div>

                        <div>
                            Lng: {params.row.longitude || "-"}
                        </div>
                    </div>
                ),
            },
            {
                field: "zoneStatus",
                headerName: "Zone Status",
                width: 170,
                sortable: false,
                filterable: false,

                renderCell: () => (
                    <Badge bg="danger">
                        Outside All Zones
                    </Badge>
                ),
            },
            {
                field: "createdAt",
                headerName: "Created On",
                minWidth: 180,
                flex: 0.8,

                valueGetter: (params) => {
                    return params.row?.createdAt || "";
                },

                renderCell: (params) => {
                    return params.row.createdAt
                        ? moment(
                            params.row.createdAt
                        ).format("YYYY/MM/DD HH:mm:ss")
                        : "-";
                },
            },
            {
                field: "action",
                headerName: "Action",
                width: 120,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,

                renderCell: (params) => (
                    <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        title="View address on map"
                        onClick={() =>
                            navigate(
                                `/admin/customers/address/${params.row.id}/location`
                            )
                        }
                    >
                        <FontAwesomeIcon
                            icon="fa-solid fa-map-location-dot"
                            className="me-1"
                        />

                        Map
                    </Button>
                ),
            },
        ],
        [navigate]
    );

    const loadAddresses = useCallback(async () => {
        setLoading(true);
        setError("");

        try {
            const response =
                await customerApi.getOutOfZoneAddresses({
                    limit: paginationModel.pageSize,
                    page: paginationModel.page,
                    search: searchText || null,
                    sort: sortModel,
                });

            if (response?.status !== "SUCCESS") {
                throw new Error(
                    response?.message
                    || "Failed to load out-of-zone addresses"
                );
            }

            const apiRows =
                response?.data?.result || [];

            const formattedRows =
                apiRows.map((row, index) => ({
                    ...row,

                    sno:
                        paginationModel.page
                        * paginationModel.pageSize
                        + index
                        + 1,
                }));

            setRows(formattedRows);

            setTotalRecords(
                Number(
                    response?.data?.totalRecords || 0
                )
            );
        } catch (requestError) {
            console.error(
                "Out-of-zone addresses request failed:",
                requestError
            );

            setError(
                requestError?.response?.data?.message
                || requestError?.message
                || "Network error while loading addresses"
            );
        } finally {
            setLoading(false);
        }
    }, [
        paginationModel.page,
        paginationModel.pageSize,
        searchText,
        sortModel,
    ]);

    useEffect(() => {
        /*
         * Small debounce for quick search.
         */
        const timer = setTimeout(() => {
            loadAddresses();
        }, searchText ? 500 : 0);

        return () => {
            clearTimeout(timer);
        };
    }, [loadAddresses, searchText]);

    const handleFilterChange = (newFilterModel) => {
        setFilterModel(newFilterModel);

        setPaginationModel((current) => ({
            ...current,
            page: 0,
        }));
    };

    return (
        <Container fluid className="py-3">
            <DynamicBreadcrumb
                pathArray={[
                    {
                        name: "Customers",
                        url: "/admin/customers/all",
                    },
                    {
                        name: "Out-of-Zone Addresses",
                        url: null,
                    },
                ]}
            />

            <div className="mb-3">
                <h4 className="mb-1">
                    Out-of-Zone Customer Addresses
                </h4>

                <div className="text-muted">
                    Active customer addresses with valid
                    coordinates that are not covered by an
                    active branch delivery zone.
                </div>
            </div>

            {loading && (
                <Alert
                    variant="info"
                    className="d-flex align-items-center"
                >
                    <Spinner
                        animation="border"
                        size="sm"
                        className="me-2"
                    />

                    Checking customer coordinates against
                    delivery zones. Please wait...
                </Alert>
            )}

            {!loading && error && (
                <Alert variant="danger">
                    <div className="d-flex align-items-center justify-content-between">
                        <span>{error}</span>

                        <Button
                            type="button"
                            variant="outline-danger"
                            size="sm"
                            onClick={loadAddresses}
                        >
                            Retry
                        </Button>
                    </div>
                </Alert>
            )}

            <div
                style={{
                    width: "100%",
                    minHeight: 580,
                }}
            >
                <DataGrid
                    rows={rows}
                    columns={columns}
                    rowCount={totalRecords}

                    loading={loading}

                    pagination
                    paginationMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={
                        setPaginationModel
                    }
                    pageSizeOptions={[15, 25, 50, 100]}

                    sortingMode="server"
                    sortModel={sortModel}
                    onSortModelChange={(newSortModel) => {
                        setSortModel(newSortModel);

                        setPaginationModel((current) => ({
                            ...current,
                            page: 0,
                        }));
                    }}

                    filterMode="server"
                    filterModel={filterModel}
                    onFilterModelChange={
                        handleFilterChange
                    }

                    slots={{
                        toolbar: GridToolbar,
                    }}

                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: {
                                debounceMs: 500,
                            },
                        },
                    }}

                    disableRowSelectionOnClick

                    getRowHeight={() => "auto"}

                    sx={{
                        backgroundColor: "#fff",
                        borderRadius: 2,

                        "& .MuiDataGrid-cell": {
                            display: "flex",
                            alignItems: "center",
                            py: 1,
                        },

                        "& .MuiDataGrid-columnHeaderTitle": {
                            fontWeight: 600,
                        },

                        "& .MuiDataGrid-overlay": {
                            backgroundColor:
                                "rgba(255,255,255,0.88)",
                        },
                    }}
                />
            </div>
        </Container>
    );
}